/**
 * Pooled shelf-label client (superuser-only).
 *
 * BE: services/plugins/ml_models_lifecycle/api/shelf_pool_views.py, mounted at
 * /ml_models_lifecycle/shelf/pool/. Same shape as the table pool client — labels live in
 * Postgres, crops arrive as short-lived presigned S3 URLs, writes are per row — with the
 * two differences the domain forces:
 *
 *  * **No stage funnel.** A shelf has ONE head, `percentage`, so "labelled" is just
 *    "has a percentage" and the views partition the zone directly.
 *  * **`percentage: null` means NOT JUDGED, and 0 means the shelf is empty.** Never
 *    render null as 0 or `?? 0` a value out of this module: an empty shelf is the case
 *    the alert exists for, and fabricating it is the one error that matters here.
 */
import { axiosInstance } from './api';

/** Labelled-ness of a crop. */
/** The four label states. These are also the FACET KEYS the API counts, which is why
 *  `masks` is not one of them: it is a different axis, and adding it here would demand a
 *  count the backend has no cheap way to produce. */
export type Labelled = 'all' | 'human' | 'ai' | 'unlabelled';

/** Kept as its own name because the request and the facet keys drifted apart once and
 *  should not silently re-merge. */
export type LabelledParam = Labelled;

/** Where the CROP came from.
 *  - `cancelled` — an operator corrected the model's rack verdict: a hard negative.
 *  - `legacy`    — the imported horeca/percentage set, a different retail domain.
 *  Only ever combined with `labelled: 'unlabelled'`: origin SPLITS the unlabelled queue
 *  rather than being an independent axis. */
export type Origin = 'collected' | 'cancelled' | 'legacy';

/** Ordering for views that HAVE a date — the unlabelled queue has none. */
export type Sort = '' | 'annotated_desc' | 'annotated_asc';

/** The one selector the annotator shows: six mutually-exclusive views in two rows —
 *  the unlabelled queue split by provenance, then the labelled one split by who. */
export type View =
  | 'cancelled' | 'collected' | 'unlabelled'
  | 'human' | 'ai' | 'all';

export const VIEW_ROWS: View[][] = [
  ['cancelled', 'collected', 'unlabelled'],
  ['human', 'ai', 'all'],
];

/** How a view maps onto the two API axes. */
export const viewParams = (v: View): { labelled: LabelledParam; origin: Origin | '' } => {
  if (v === 'cancelled') return { labelled: 'unlabelled', origin: 'cancelled' };
  if (v === 'collected') return { labelled: 'unlabelled', origin: 'collected' };
  return { labelled: v as LabelledParam, origin: '' };
};

/** Fullness is entered in 5% steps: finer than that is not a judgement a human makes
 *  reliably from a crop, and the model is a regressor — the extra precision would be
 *  noise presented as signal. */
export const PERCENT_STEP = 5;

export interface ShelfPoolZone {
  zone_id: number;
  zone: string;
  /** 0 for a configured-but-never-collected zone. Those are listed on purpose: a zone
   *  has to be selectable before «Собрать кропы» can fill it. */
  total: number;
  todo: number;
  /** Hard negatives still waiting for a verdict — the highest-value work in the zone. */
  cancelled_todo: number;
  /** %-predicting shelves configured in the zone. A zone at (0) with shelves is worth
   *  seeding; one with no shelves is not set up for this model at all. */
  shelves: number;
}

export interface BatchZone { zone_id: number; zone: string; count: number }

export interface ShelfPoolItem {
  id: number;
  zone_id: number;
  shelf_id: number | null;
  /** Rack + shelf, or the crop filename for legacy rows that have no shelf at all. */
  shelf: string;
  camera_id: number | null;
  camera: string | null;
  frame_id: number | null;
  crop_file: string;
  crop_url: string;
  /** null = NO verdict yet. 0 = the shelf is empty. These are not the same thing. */
  percentage: number | null;
  origin: Origin;
  /** Only in batch mode, where the grid spans every zone. */
  zone?: string;
  source: 'user' | 'ai' | null;
  annotated_by: string | null;
  annotated_at: string | null;
  /** Whether SAM's objects are stored for this crop — i.e. whether the mask editor has
   *  anything to open. Carried per row so the grid never has to fetch masks to find out
   *  there are none. */
  has_masks: boolean;
  /** What SAM's objects cover on their own — the union of everything two or more prompts
   *  agreed on, the set the editor opens with. Shown BESIDE the stored verdict, not
   *  instead of it: where they differ is the question a labeller is there to answer. */
  mask_percentage: number | null;
  /** Полезная площадь полки (калибровка полигонов); null — полку ещё не размечали,
   *  нет ключа — бэкенд старее фронта. */
  useful_area?: ShelfUsefulArea | null;
}

/** Где на полке физически может лежать товар: контуры в долях кропа 0..1. include — полезно
 *  обведённое, exclude — всё, кроме обведённого. stale — полигон полки сдвинули после разметки. */
export interface ShelfUsefulArea {
  mode: 'include' | 'exclude';
  polygons: number[][][];
  pct: number | null;
  stale: boolean;
}

export interface ShelfPoolSamples {
  total: number;
  page: number;
  page_size: number;
  labelled: LabelledParam;
  origin: Origin | '';
  /** List mode (deeplink `?ids=`): how many ids the list names. */
  list?: number;
  zone_total: number | null;
  facets: {
    labelled: Record<Labelled, number>;
    shelves: { shelf_id: number | null; shelf: string; count: number }[];
    /** Splits of the UNLABELLED queue by provenance. Each is its own total, never
     *  narrowed by the current selection. */
    unlabelled_origins: Record<Origin, number>;
    /** Batch mode only: how many crops each zone contributed. */
    batch_zones?: BatchZone[];
  };
  items: ShelfPoolItem[];
}

export const getShelfPoolZones = () =>
  axiosInstance.get<{ zones: ShelfPoolZone[] }>('/ml_models_lifecycle/shelf/pool/zones/');

export const getShelfPoolSamples = (params: {
  collection?: string;
  zone_id: number | null; labelled: LabelledParam;
  /** Only crops SAM's objects have been computed for — the queue the mask editor can
   *  work. Combines with every view rather than replacing them. */
  has_masks?: boolean;
  origin?: Origin | '';
  sort?: Sort;
  /** >0 switches to batch mode: N unlabelled crops from EVERY zone. The batch is the
   *  selection, so zone/view/shelf filters do not apply to it. */
  batch?: number;
  /** Explicit label ids (the audit deeplink): the list IS the selection, shown in this
   *  order; zone/view/shelf/batch do not apply. */
  ids?: number[];
  shelf_id?: number | null; page?: number; page_size?: number;
}) =>
  axiosInstance.get<ShelfPoolSamples>('/ml_models_lifecycle/shelf/pool/samples/', {
    params: {
      collection: params.collection || undefined,
      ...(params.ids?.length ? { ids: params.ids.join(',') }
        : params.batch ? { batch: params.batch } : { zone_id: params.zone_id }),
      labelled: params.ids?.length ? 'all' : params.labelled,
      ...(params.has_masks ? { has_masks: 1 } : {}),
      ...(params.origin && !params.batch ? { origin: params.origin } : {}),
      ...(params.sort ? { sort: params.sort } : {}),
      ...(params.shelf_id ? { shelf_id: params.shelf_id } : {}),
      page: params.page ?? 1, page_size: params.page_size ?? 200,
    },
  });

/** `percentage: null` retracts a verdict — the BE clears provenance with it, so the
 *  crop genuinely returns to the unlabelled queue instead of looking reviewed. */
export type ShelfLabelDelta = { id: number; percentage: number | null };

export const shelfPoolAnnotate = (items: ShelfLabelDelta[]) =>
  axiosInstance.post<{ saved: number }>(
    '/ml_models_lifecycle/shelf/pool/annotate/', { items });

/** Soft delete (reversible): the crop leaves training + the queue but the row remains. */
export const shelfPoolDelete = (ids: number[], deleted = true) =>
  axiosInstance.post<{ updated: number; deleted: boolean }>(
    '/ml_models_lifecycle/shelf/pool/delete/', { ids, deleted });

export interface DagsterRunRef {
  run_id: string; status: string; url: string;
  /** true = a run of this job was already going; the BE returned it instead of starting another. */
  already_running?: boolean;
}

export interface ShelfTrainStatus {
  labelled_now: number;
  unlabelled: number;
  /** Counts per 10% band. The pool is heavily skewed to full shelves, so this is the
   *  number that tells you whether a retrain can possibly learn the empty end. */
  by_bucket: Record<string, number>;
  /** null until the FIRST run trained from a pooled snapshot: earlier runs counted their
   *  baseline off the per-zone datasets, a different scale, so no honest delta exists. */
  new_since_last: number | null;
  last_run: {
    created_at: string;
    trained_up_to: number;
    snapshot_items: number | null;
    snapshot_version_id: string;
    model_version: string;
    dagster_run_url: string;
  } | null;
}

export const getShelfTrainStatus = () =>
  axiosInstance.get<ShelfTrainStatus>('/ml_models_lifecycle/shelf/pool/train-status/');

export const shelfPoolRetrain = () =>
  // Dagster's launchRun answers in ~25-60 s — past the global 30 s timeout, which
  // reported a run that was in fact training as "failed" (28.10.2026).
  axiosInstance.post<DagsterRunRef>('/ml_models_lifecycle/shelf/pool/retrain/', undefined, { timeout: 120000 });

// ─── seeding the pool: collecting UNLABELLED crops for a zone ────────────────
// The zone comes from the annotator's own picker, which lists configured-but-empty zones
// at (0) — so there is no zone list here. Nothing here labels anything: crops arrive with
// `percentage` NULL, which is what puts them in «Неразмеченные» and keeps them out of
// training until a human has judged them.

export interface ShelfCollectPrecheck {
  zone_id: number;
  shelves: number;
  /** Domain-neutral count the shared dialog reads (= `shelves` here). */
  objects: number;
  /** Crops the window actually holds — the ceiling on a run, and usually what decides
   *  the outcome rather than the requested size. */
  available: number;
  pooled: number;
  date_from: string;
  date_to: string;
  shelves_list: { shelf_id: number; name: string }[];
}

export interface ShelfCollectRun {
  id: number;
  zone_id: number;
  zone?: string;
  status: 'running' | 'done' | 'failed';
  requested: number;
  available: number;
  collected: number;
  /** Already pooled, so skipped. An all-skip run means the window is mined out — widen
   *  the dates rather than pressing again. */
  skipped_existing: number;
  failed: number;
  per_shelf: Record<string, number>;
  error: string;
  launched_by: string;
  date_from: string;
  date_to: string;
  created_at: string;
  finished_at: string | null;
}

export const getShelfCollectPrecheck = (zone_id: number, days: number) =>
  axiosInstance.get<ShelfCollectPrecheck>(
    '/ml_models_lifecycle/shelf/pool/collect/precheck/', { params: { zone_id, days } });

/** Queue a collection. Returns the run row immediately — the work happens in celery,
 *  because a shelf crop is REBUILT from its full frame, not copied. */
export const launchShelfCollect = (zone_id: number, target: number, days: number) =>
  axiosInstance.post<ShelfCollectRun>(
    '/ml_models_lifecycle/shelf/pool/collect/', { zone_id, target, days });

export const getShelfCollectRuns = (zone_id?: number, limit = 30) =>
  axiosInstance.get<{ runs: ShelfCollectRun[] }>(
    '/ml_models_lifecycle/shelf/pool/collect/runs/',
    { params: { ...(zone_id ? { zone_id } : {}), limit } });

/** One prompt's result on the bench: what it found, what the size filter threw away. */
export interface SamProbePrompt {
  prompt: string;
  instances: number;
  kept: number;
  dropped: number;
  coverage: number;
  error: string | null;
}

export interface SamProbeResult {
  width: number;
  height: number;
  threshold: number;
  max_instance_frac: number;
  calls: number;
  /** Painted pixels and the frame's total — the arithmetic behind `coverage`. */
  union_px: number;
  total_px: number;
  /** Share of the picture the union covers. On a POOLED CROP this is the shelf's
   *  fullness itself — the crop is the shelf — and is exactly what SAM writes as the
   *  label. On an arbitrary pasted picture it is only coverage of that picture. */
  coverage: number;
  per_prompt: SamProbePrompt[];
  /** The image AS THE MODEL SAW IT (resized), so it lines up with the overlay. */
  original: string | null;
  overlay: string | null;
}

/** Try SAM 3 prompts on one picture. Writes nothing — a bench, not a labelling call.
 *  Costs one Replicate call per prompt, so the caller should not fire it per keystroke. */
export const shelfSamProbe = (body: {
  image?: string;
  item_id?: number;
  prompts: string;
  threshold?: number;
  max_instance_frac?: number;
}) => axiosInstance.post<SamProbeResult>('/ml_models_lifecycle/shelf/pool/sam-probe/', body);

/** The settings the fleet pass runs with. Served, not hard-coded, so the bench cannot
 *  drift from production when a prompt is added. */
export interface SamDefaults {
  /** What the panel opens with — broad words, quick to read. */
  prompts: string[];
  /** The fourteen the fleet pass actually runs, for comparing against. */
  production_prompts: string[];
  threshold: number;
  max_instance_frac: number;
  /** The rack-crop cut, shown for contrast — not what the bench uses. */
  production_max_instance_frac: number;
  fallback_threshold: number;
  max_prompts: number;
}

export const getSamDefaults = () =>
  axiosInstance.get<SamDefaults>('/ml_models_lifecycle/shelf/pool/sam-defaults/');

/** One prompt's objects on a crop, in the UPSCALED crop's pixels. */
export interface ShelfMaskPrompt {
  prompt: string;
  threshold: number;
  width: number;
  height: number;
  coverage: number;
  areas: number[];
  contours: number[][][];
}

export interface ShelfMasks {
  item_id: number;
  /** Decides which prompts start ticked — see DEFAULT_PROMPTS_FOR. */
  rack_type: string;
  percentage: number | null;
  source: 'user' | 'ai' | null;
  annotated_by: string | null;
  /** What a labeller kept last time. null = never edited; [] = "nothing on this shelf". */
  contours: number[][][] | null;
  useful_area?: ShelfUsefulArea | null;
  /** Ordered by coverage, smallest first — the ladder the slider walks. */
  prompts: ShelfMaskPrompt[];
  computed: boolean;
}

export const getShelfMasks = (itemId: number) =>
  axiosInstance.get<ShelfMasks>('/ml_models_lifecycle/shelf/pool/masks/',
                                { params: { item_id: itemId } });

export const computeShelfMasks = (ids: number[]) =>
  axiosInstance.post<{ queued: number }>(
    '/ml_models_lifecycle/shelf/pool/masks/compute/', { ids });

/** Re-cut the shelf from its ORIGINAL frame with room around it and segment that.
 *  For a crop SAM answered with nothing: re-running `computeShelfMasks` would put the
 *  same stored pixels through the same prompts and return the same emptiness. */
export const recutShelfMasks = (ids: number[], pad?: number) =>
  axiosInstance.post<{ queued: number }>(
    '/ml_models_lifecycle/shelf/pool/masks/recut/', { ids, pad });

export const saveShelfMasks = (itemId: number, contours: number[][][]) =>
  axiosInstance.post<{ saved: number; percentage: number; objects: number }>(
    '/ml_models_lifecycle/shelf/pool/masks/save/', { item_id: itemId, contours });


/** Superseded by agreement-based grouping: an object several prompts found lands in the
 *  BASE regardless of rack type, and a prompt's box now adds only what is unique to it.
 *  `packaging` on a bakery rack still paints the liner between the buns — it just no
 *  longer needs a per-rack allow-list, because nothing else agreed with it and so it sits
 *  behind its own tick. Kept exported for one release in case a caller still imports it.
 *  @deprecated */
export const DEFAULT_PROMPTS_FOR = (rackType: string): string[] =>
  (rackType === 'Выпечка'
    ? ['food', 'groceries']
    : ['food', 'packaging', 'groceries', 'bottle', 'can']);

/** Launch a Dagster run that segments unlabelled crops and records the AI verdict.
 *  Returns immediately with a run id — the work itself takes about a minute a crop. */
export const launchShelfPrelabel = (limit: number, zoneId: number | null) =>
  axiosInstance.post<{ run_id: string; url: string; limit: number }>(
    '/ml_models_lifecycle/shelf/pool/prelabel/',
    { limit, zone_id: zoneId ?? 0 });
